import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BadgeCheck,
  Download,
  HardHat,
  MapPin,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Star,
  UserCheck,
  Users,
} from 'lucide-react';
import { LumperStatusBadge, PageHead } from '../components/bits.jsx';
import { HBarList } from '../components/charts.jsx';
import { Avatar, Badge, Button, Card, CardBody, CardHead, cx, EmptyState, Field, Input, Loading, Modal, PhoneInput, Progress, SearchInput, Select, Stars, Stat, Switch, useToast } from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { compactMoney, dateLabel, money, num, relative, titleCase } from '../lib/format.js';

const STATUSES = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Available to work' },
  { value: 'approved', label: 'Approved, not working yet' },
  { value: 'applied', label: 'Just applied' },
  { value: 'screening', label: 'Being checked' },
  { value: 'paused', label: 'Taking a break' },
  { value: 'suspended', label: 'Stopped' },
  { value: 'rejected', label: 'Not taken on' },
];

const TIERS = [
  { value: 'all', label: 'Any tier' },
  { value: 'crew_lead', label: 'People who run crews' },
  { value: 'pro', label: 'Our best' },
  { value: 'standard', label: 'Everyone else' },
];

const SORTS = [
  { value: 'applied:desc', label: 'Newest signups first' },
  { value: 'applied:asc', label: 'Oldest signups first' },
  { value: 'active:desc', label: 'Active most recently' },
  { value: 'rating:desc', label: 'Best rated' },
  { value: 'jobs:desc', label: 'Most jobs done' },
  { value: 'pay30:desc', label: 'Earned the most' },
  { value: 'name:asc', label: 'Name' },
];

export default function Lumpers() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 300);
  const [addOpen, setAddOpen] = useState(false);

  const status = params.get('status') || 'all';
  const tier = params.get('tier') || 'all';
  const sort = params.get('sort') || 'active:desc';
  const available = params.get('available') === 'true';
  const skill = params.get('skill') || '';
  const [sortKey, sortDir] = sort.split(':');

  const filters = useMemo(
    () => ({ status, tier, sort: sortKey, dir: sortDir, q: debounced || undefined, available: available ? 'true' : undefined, skill: skill || undefined, limit: 120 }),
    [status, tier, sortKey, sortDir, debounced, available, skill],
  );

  const { data, meta, loading, refresh } = useApi('/lumpers', { params: filters });
  const { data: metaData } = useApi('/lumpers/meta');
  const lumpers = data || [];

  const summary = meta?.summary || {};
  const total = meta?.total || lumpers.length;
  const activeCount = summary.active || 0;
  const rosterPay30 = lumpers.reduce((sum, l) => sum + Number(l.pay30d || 0), 0);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHead
        title="Our lumpers"
        subtitle={`${num(total)} people on our books · ${num(activeCount)} ready to work right now`}
        icon={HardHat}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Refresh</Button>
            <Button variant="outline" size="sm" icon={ShieldAlert} onClick={() => navigate('/app/onboarding')}>
              New people to look at
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={() => setAddOpen(true)}>Add someone</Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Ready to work" icon={UserCheck} value={num(activeCount)} tone="green" foot={`${num(summary.applied || 0)} applied · ${num(summary.screening || 0)} screening`} />
        <Stat label="Paid out in 30 days" icon={HardHat} value={compactMoney(rosterPay30)} tone="blue" foot="to the lumpers in this view" />
        <Stat
          label="Average rating"
          icon={Star}
          value={(lumpers.reduce((s, l) => s + Number(l.rating || 0), 0) / Math.max(lumpers.length, 1)).toFixed(2)}
          tone="amber"
          foot="across this view"
        />
        <Stat
          label="Paperwork missing"
          icon={ShieldAlert}
          value={num(lumpers.filter((l) => l.missingDocs > 0).length)}
          tone={lumpers.some((l) => l.missingDocs > 0) ? 'red' : 'green'}
          foot="we need these before they can work"
        />
      </div>

      <div className="filter-bar">
        <SearchInput value={query} onChange={setQuery} placeholder="Search by name, city or phone number…" style={{ minWidth: 240, maxWidth: 320 }} />
        <Select className="select-sm" value={status} onChange={(e) => setParam('status', e.target.value)} options={STATUSES} />
        <Select className="select-sm" value={tier} onChange={(e) => setParam('tier', e.target.value)} options={TIERS} />
        <Select
          className="select-sm"
          value={skill}
          onChange={(e) => setParam('skill', e.target.value)}
          options={[{ value: '', label: 'Any skill' }, ...(metaData?.skills || []).map((s) => ({ value: s, label: s }))]}
        />
        <Switch label="Free right now" checked={available} onChange={(e) => setParam('available', e.target.checked ? 'true' : '')} />
        <div className="grow" />
        <Select
          className="select-sm"
          value={sort}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            next.set('sort', e.target.value);
            setParams(next, { replace: true });
          }}
          options={SORTS}
        />
      </div>

      {loading && !lumpers.length ? (
        <Loading rows={8} />
      ) : lumpers.length ? (
        <Card>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Person</th>
                  <th
                    className={cx('narrow', 'sortable', sort.startsWith('applied:') && 'sorted')}
                    onClick={() => setParam('sort', sort === 'applied:asc' ? 'applied:desc' : 'applied:asc')}
                    title="Click to sort by the day they signed up"
                  >
                    Signed up
                    <span className="sort-ind">{sort === 'applied:asc' ? '↑' : sort === 'applied:desc' ? '↓' : '↕'}</span>
                  </th>
                  <th className="narrow">Status</th>
                  <th className="narrow">Level</th>
                  <th className="narrow">Rating</th>
                  <th className="right">Jobs done</th>
                  <th className="right">Turns up</th>
                  <th className="right">Says yes</th>
                  <th className="right">We pay</th>
                  <th className="right">Earned</th>
                  <th className="narrow">Where they work</th>
                  <th className="narrow">Paperwork</th>
                </tr>
              </thead>
              <tbody>
                {lumpers.map((lumper) => (
                  <tr key={lumper.id} className="clickable" onClick={() => navigate(`/app/lumpers/${lumper.id}`)}>
                    <td>
                      <div className="row gap-10">
                        <Avatar name={`${lumper.firstName} ${lumper.lastName}`} tone={lumper.photoColor} />
                        <div className="col gap-2 truncate">
                          <span className="row gap-6">
                            <span className="truncate" style={{ fontWeight: 600 }}>{lumper.firstName} {lumper.lastName}</span>
                            {lumper.jobsCompleted > 40 ? <Badge tone="teal" size="sm">Old hand</Badge> : null}
                            {lumper.crewSize ? <Badge tone="amber" size="sm">Crew of {lumper.crewSize}</Badge> : null}
                          </span>
                          <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>
                            {lumper.code} · {lumper.kind === 'individual' ? 'Individual' : lumper.crewName || titleCase(lumper.kind)}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="narrow">
                      <div className="col gap-2">
                        <span style={{ fontSize: 'var(--fs-xs)' }}>{dateLabel(lumper.appliedAt)}</span>
                        <span className="dimmer" style={{ fontSize: 10 }}>{relative(lumper.appliedAt)}</span>
                      </div>
                    </td>
                    <td className="narrow"><LumperStatusBadge status={lumper.status} size="sm" /></td>
                    <td className="narrow">
                      <Badge tone={lumper.tier === 'crew_lead' ? 'amber' : lumper.tier === 'pro' ? 'teal' : 'muted'} size="sm">
                        {lumper.tier === 'crew_lead' ? 'Crew lead' : titleCase(lumper.tier)}
                      </Badge>
                    </td>
                    <td className="narrow"><Stars value={lumper.rating} size={11} /></td>
                    <td className="right num">{num(lumper.jobsCompleted)}</td>
                    <td className="right num">{lumper.onTimeRate ? `${Number(lumper.onTimeRate).toFixed(0)}%` : '—'}</td>
                    <td className="right num">{lumper.acceptanceRate ? `${Number(lumper.acceptanceRate).toFixed(0)}%` : '—'}</td>
                    <td className="right num">{money(lumper.payRate, { decimals: 2 })}</td>
                    <td className="right num">{money(lumper.pay30d)}</td>
                    <td className="narrow">
                      <div className="col gap-2">
                        <span className="row gap-4 dim" style={{ fontSize: 'var(--fs-xs)' }}>
                          <MapPin size={11} /> {lumper.city || '—'}
                        </span>
                        <span className="dimmer" style={{ fontSize: 10 }}>{lumper.radiusMiles} mi radius</span>
                      </div>
                    </td>
                    <td className="narrow">
                      {lumper.missingDocs ? (
                        <Badge tone="red" size="sm">{lumper.missingDocs} missing</Badge>
                      ) : (
                        <Badge tone="green" size="sm" dot>clear</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <Card>
          <EmptyState icon={Users} title="Nobody matches this">
            Try clearing the filters, or invite people to apply from the public site.
          </EmptyState>
        </Card>
      )}

      <AddLumperModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        skills={metaData?.skills || []}
        equipment={metaData?.equipment || []}
        onCreated={(lumper) => {
          toast.ok(`${lumper.firstName} ${lumper.lastName} added`, 'They can be assigned from the dispatch board.');
          refresh();
          navigate(`/app/lumpers/${lumper.id}`);
        }}
      />
    </>
  );
}

function AddLumperModal({ open, onClose, onCreated, skills = [], equipment = [] }) {
  const toast = useToast();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    state: 'FL',
    zip: '',
    kind: 'individual',
    tier: 'standard',
    payRate: 24,
    payType: 'hourly',
    radiusMiles: 25,
    status: 'active',
    skills: [],
    equipment: [],
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggle = (key, value) =>
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v !== value) : [...prev[key], value],
    }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title="Add someone to the crew"
      subtitle="For people you took on in person or over the phone"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              if (!form.firstName || !form.lastName) {
                toast.err('A first and last name are required');
                return;
              }
              setBusy(true);
              try {
                const payload = await api.post('/lumpers', { ...form, payRate: Number(form.payRate), radiusMiles: Number(form.radiusMiles) });
                onCreated(payload.data);
                onClose();
              } catch (err) {
                toast.err('Could not add the lumper', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Add them
          </Button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="grid grid-2" style={{ gap: 14 }}>
          <Field label="First name" required><Input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></Field>
          <Field label="Last name" required><Input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></Field>
          <Field label="Phone"><PhoneInput value={form.phone} onChange={(value) => set('phone', value)} placeholder="(305) 123-4567" /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set('city', e.target.value)} /></Field>
          <Field label="State"><Input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} /></Field>
          <Field label="ZIP"><Input value={form.zip} onChange={(e) => set('zip', e.target.value)} /></Field>
          <Field label="How far will they travel? (miles)"><Input type="number" value={form.radiusMiles} onChange={(e) => set('radiusMiles', e.target.value)} /></Field>
          <Field label="Are they…">
            <Select
              value={form.kind}
              onChange={(e) => set('kind', e.target.value)}
              options={[
                { value: 'individual', label: 'Individual lumper' },
                { value: 'crew', label: 'Crew' },
                { value: 'vendor', label: 'Vendor / labor company' },
              ]}
            />
          </Field>
          <Field label="Level">
            <Select
              value={form.tier}
              onChange={(e) => set('tier', e.target.value)}
              options={[
                { value: 'standard', label: 'Standard' },
                { value: 'pro', label: 'Pro' },
                { value: 'crew_lead', label: 'Crew lead' },
              ]}
            />
          </Field>
          <Field label="What we pay"><Input className="mono" type="number" value={form.payRate} onChange={(e) => set('payRate', e.target.value)} /></Field>
          <Field label="Paid by">
            <Select
              value={form.payType}
              onChange={(e) => set('payType', e.target.value)}
              options={[
                { value: 'hourly', label: 'Per hour' },
                { value: 'per_pallet', label: 'Per pallet' },
                { value: 'per_job', label: 'Per job' },
              ]}
            />
          </Field>
        </div>

        <div>
          <div className="label mb-8">Skills</div>
          <div className="wrap-gap">
            {skills.map((skill) => (
              <button
                key={skill}
                type="button"
                className={cx('skill-pill', form.skills.includes(skill) && 'on')}
                onClick={() => toggle('skills', skill)}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="label mb-8">Equipment</div>
          <div className="wrap-gap">
            {equipment.map((item) => (
              <button
                key={item}
                type="button"
                className={cx('skill-pill', form.equipment.includes(item) && 'on')}
                onClick={() => toggle('equipment', item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
